import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomDomainRouter } from '@/hooks/useCustomDomainRouter';
import { SafeLoading } from '@/components/ui/safe-loading';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import FloatingCartButton from '@/components/supplier-menu/FloatingCartButton';

// Lazy load SupplierStorePage
const SupplierStorePage = React.lazy(() => import('@/pages/SupplierStorePage'));

interface CustomDomainWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that detects custom domain access and routes to the appropriate supplier store.
 * If accessing via a custom domain (e.g., cardapio.ameup.com.br), it bypasses normal routing
 * and directly renders the SupplierStorePage for that supplier.
 */
export const CustomDomainWrapper: React.FC<CustomDomainWrapperProps> = ({ children }) => {
  const location = useLocation();
  const { isCustomDomain, supplierSlug, isLoading, error } = useCustomDomainRouter();
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const shouldRenderStoreAtCustomDomain = isCustomDomain && supplierSlug && (
    normalizedPath === '/' ||
    normalizedPath === '/loja' ||
    normalizedPath === `/loja/${supplierSlug}`
  );
  const shouldShowDomainError = isCustomDomain && !supplierSlug && (
    normalizedPath === '/' ||
    normalizedPath === '/loja'
  );

  // Show loading while checking domain
  if (isLoading) {
    return <SafeLoading size="lg" message="Carregando..." />;
  }

  // Only intercept root/store paths on custom domains.
  // Let React Router handle routes like /cardapio/:supplierSlug/:productSlug normally.
  if (shouldRenderStoreAtCustomDomain) {
    return (
      <div className="min-h-screen bg-background">
        {/* Nesta rota o wrapper devolve só a loja (sem os filhos do App): os componentes globais precisam
            ser montados aqui, senão o carrinho flutuante e os avisos (toasts) não aparecem no domínio próprio. */}
        <Toaster />
        <Sonner />
        <FloatingCartButton />
        <Suspense fallback={<SafeLoading size="lg" message="Carregando loja..." />}>
          <SupplierStorePage supplierSlugOverride={supplierSlug} />
        </Suspense>
      </div>
    );
  }

  // If it's a custom domain but no supplier found, show error
  if (shouldShowDomainError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8">
          <h1 className="text-2xl font-bold text-foreground">Domínio não configurado</h1>
          <p className="text-muted-foreground">
            {error || 'Este domínio não está vinculado a nenhuma loja.'}
          </p>
          <p className="text-sm text-muted-foreground">
            Se você é o proprietário, verifique as configurações de domínio no painel do fornecedor.
          </p>
        </div>
      </div>
    );
  }

  // Normal routing - render children (Routes)
  return <>{children}</>;
};
